import type { TideHeight } from "@/lib/fishing-data";

const TZ = "America/Anchorage";

function akDateKey(iso: string) {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: TZ });
}

/** Hours (0–24, fractional) of an ISO time in Alaska local time. */
function akHours(iso: string) {
  const parts = new Date(iso).toLocaleTimeString("en-GB", {
    timeZone: TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const [h, m] = parts.split(":").map(Number);
  return h + m / 60;
}

type Props = {
  heights: TideHeight[];
  /** Current epoch ms, for the "now" marker. */
  now: number;
  className?: string;
};

/** Simple day timeline of predicted tide height vs. time (Alaska local day). */
export function TideDayChart({ heights, now, className }: Props) {
  const todayKey = new Date(now).toLocaleDateString("en-CA", { timeZone: TZ });
  const points = heights
    .filter((h) => akDateKey(h.t) === todayKey)
    .map((h) => ({ x: akHours(h.t), y: Number(h.v) }))
    .filter((p) => Number.isFinite(p.y))
    .sort((a, b) => a.x - b.x);

  if (points.length < 2) return null;

  const W = 900;
  const H = 260;
  const padX = 40;
  const padTop = 24;
  const padBottom = 44;

  const ys = points.map((p) => p.y);
  const minY = Math.min(...ys, 0);
  const maxY = Math.max(...ys);
  const span = maxY - minY || 1;

  const sx = (x: number) => padX + (x / 24) * (W - padX * 2);
  const sy = (y: number) =>
    padTop + (1 - (y - minY) / span) * (H - padTop - padBottom);

  const line = points.map((p) => `${sx(p.x)},${sy(p.y)}`).join(" ");
  const area = `${sx(points[0].x)},${sy(minY)} ${line} ${sx(points[points.length - 1].x)},${sy(minY)}`;
  const nowX = sx(akHours(new Date(now).toISOString()));

  return (
    <figure className={className}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Predicted tide height over the course of today"
      >
        <polygon points={area} fill="var(--color-ember)" opacity="0.18" />
        <polyline
          points={line}
          fill="none"
          stroke="var(--color-ember)"
          strokeWidth={4}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <line
          x1={padX}
          x2={W - padX}
          y1={sy(minY)}
          y2={sy(minY)}
          stroke="currentColor"
          opacity="0.25"
          strokeWidth={2}
        />
        <line
          x1={nowX}
          x2={nowX}
          y1={padTop}
          y2={sy(minY)}
          stroke="currentColor"
          opacity="0.55"
          strokeWidth={2}
          strokeDasharray="8 8"
        />
        {[0, 6, 12, 18, 24].map((h) => (
          <text
            key={h}
            x={sx(h)}
            y={H - 12}
            textAnchor="middle"
            fill="currentColor"
            opacity="0.6"
            fontSize={20}
          >
            {h === 0 ? "12a" : h === 12 ? "12p" : h === 24 ? "12a" : h < 12 ? `${h}a` : `${h - 12}p`}
          </text>
        ))}
      </svg>
    </figure>
  );
}
